/**
 * iter-121.132.b — Shared "Apply Dashboard Theme" pill.
 *
 * SA-only header affordance. Re-fetches the tenant's effective
 * Dashboard Theme from settings and re-stamps the --chart-N /
 * --dash-shadow / data-skin CSS vars on :root. Useful when the SA
 * tweaked the theme in another tab and wants the change visible here
 * without a full page reload.
 *
 * Extracted from `DashboardDetailView.jsx` so the Prebuilt template
 * preview page (`GalleryPreview.jsx`) can mount the same pill — the
 * Prebuilt preview rides a different route (/dashboards/preview/:id),
 * so the DashboardDetailView header doesn't render there.
 *
 * Caller is responsible for ALL visibility gating (role checks,
 * dashboard-shape checks). The pill itself just exposes the action.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Palette } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { applyDashboardTheme } from "@/lib/settings-context";


export default function ApplyThemePill({ testId = "prebuilt-apply-theme-btn" }) {
    const [busy, setBusy] = React.useState(false);
    const onApply = async () => {
        setBusy(true);
        try {
            const r = await api.get("/settings/dashboard-theme");
            const cfgToApply = r?.data?.effective_config || r?.data?.config;
            if (cfgToApply && r?.data?.registry) {
                applyDashboardTheme(cfgToApply, r.data.registry);
                toast.success("Dashboard theme applied");
            } else {
                toast.info("No theme configured yet — open Settings → Dashboard Theme");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to apply theme");
        } finally {
            setBusy(false);
        }
    };
    return (
        <Button
            variant="outline" size="sm"
            className="rounded-sm h-8 text-xs"
            onClick={onApply}
            disabled={busy}
            data-testid={testId}
        >
            <Palette className="h-3 w-3 mr-1.5" />
            {busy ? "Applying…" : "Apply Theme"}
        </Button>
    );
}
